import {Component, effect, inject, Input, OnInit} from '@angular/core';
import {BarChartModule, Color, NgxChartsModule, ScaleType} from "@swimlane/ngx-charts";
import {AssetDetailsComponent, lastSeenWithinThreshold} from "src/app/presentation/common/asset-details.component";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {AssetSampleApiClient} from "src/app/infrastructure/moovement-api/web-api/asset-sample.api-client";
import {Samples} from "src/app/infrastructure/moovement-api/models/samples";
import {
  PrecipitationSvgComponent
} from "src/app/presentation/common/icons/precipitation-svg/precipitation-svg.component";
import {fourteenDays, sevenDays, thirtyDays, threeDays, twentyFourHours} from "src/app/presentation/common/globals";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {FixedAsset} from "src/app/infrastructure/moovement-api/models/asset";
import {FixedSamplePointsStore} from 'src/app/domain/assets/fixed-sample-points/fixed-sample-points.store';
import {AssetMetadataComponent} from "src/app/presentation/common/asset-metadata/asset-metadata.component";

@Component({
  selector: 'app-precipitation-point-details',
  templateUrl: './precipitation-sample-point-details.component.html',
  imports: [
    BarChartModule,
    NgxChartsModule,
    PrecipitationSvgComponent,
    GenericButtonComponent,
    AssetMetadataComponent
  ],
  styleUrl: './precipitation-sample-point-details.component.css'
})
export class PrecipitationPointDetailsComponent extends AssetDetailsComponent<FixedAsset> implements OnInit {

  @Input() unit!: string
  protected highestBarValue: number = 5
  protected highestLineValue: number = 5
  protected xAxisTicks: string[] = []
  protected selectedDateRange: number = fourteenDays
  protected selectedDateRangeName: string = ''
  protected dateRanges: Map<string, number> = new Map<string, number>()
  protected selectableDateRanges: string[] = []
  protected lineDataset: { name: string; series: { name: string, value: number[] }[] }[] = []
  protected barDataset: { name: string, value: number }[] = []
  protected showLegend = false;
  protected buttonText = 'Show accumulated'
  protected amount = true
  protected accumulated = false
  protected scheme: Color = {
    name: "",
    selectable: false,
    group: ScaleType.Ordinal,
    domain: ['#6291d8']
  }
  protected barScheme: Color = {
    name: "",
    selectable: false,
    group: ScaleType.Ordinal,
    domain: ['#9436f8']
  }

  private fixedSamplePointsStore = inject(FixedSamplePointsStore);

  constructor(
    dataSynchronisationService: DataSynchronisationService,
    dataSubscriptionService: DataSubscriptionService,
    viewportService: ViewportService,
    private assetSampleApiClient: AssetSampleApiClient
  ) {
    super(dataSynchronisationService, dataSubscriptionService, viewportService)

    this.samplesKey.set('precipitationPointAssetSamples');

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      this.asset.set(this.fixedSamplePointsStore.entityMap()[assetId]);
    });

    effect(() => {
      if (!this.lastAssetSample()) return;
      this.fetchData('14 Days')
    });
  }

  format(tooltipText: any) {
    return (Math.round(Number.parseFloat(tooltipText.toString()) * 100) / 100).toFixed(2) + ` ${this.unit}`;
  }

  ngOnInit() {
    this.dateRanges.set('24 Hours', twentyFourHours).set('3 Days', threeDays).set('7 Days', sevenDays).set('14 Days', fourteenDays).set('30 Days', thirtyDays)
  }

  fetchData(dateRangeName: string) {
    this.loading = true

    this.selectedDateRange = this.dateRanges.get(dateRangeName)!;
    this.selectedDateRangeName = dateRangeName
    this.selectableDateRanges = Array.from(this.dateRanges).filter(dateRange => dateRange[0] != dateRangeName).map(it => it[0])
    const lastSampleInstant = this.lastAssetSample()?.sample?.instant != undefined ? new Date(this.lastAssetSample()?.sample?.instant!) : new Date()
    const lastSampleInstantMinusDateRange = new Date()
    let timeInMilliseconds = lastSampleInstant.getTime();
    timeInMilliseconds -= this.selectedDateRange
    lastSampleInstantMinusDateRange.setTime(timeInMilliseconds);
    this.xAxisTicks = []
    this.assetSampleApiClient.getAssetSamplesBetween<Samples.PrecipitationSample>('Precipitation', [this.id()!], lastSampleInstantMinusDateRange, lastSampleInstant)
      .subscribe({
        next: (assetSamples) => {
          const barSeries: { name: string, value: number }[] = this.getCumulativeSeries(assetSamples)
          const lineSeries: { name: string, value: number[] }[] = []
          assetSamples.forEach(assetSample => {
            const amount = assetSample.sample?.value?.amount!
            const date = new Date(assetSample.sample!.instant!)
            if (!this.xAxisTicks.includes(date.toDateString())) {
              this.xAxisTicks.push(date.toDateString())
            }
            if (lineSeries.some(entry => entry.name == date.toDateString())) {
              lineSeries.find(entry => entry.name == date.toDateString())?.value.push(amount)
            } else {
              lineSeries.push({
                name: date.toISOString(),
                value: [amount]
              })
            }
          })
          this.barDataset = barSeries
          this.lineDataset = [
            {
              name: this.asset!.name! + '-per-datetime',
              series: lineSeries
            }
          ]
          this.loading = false;
        }
      })
  }

  formatY(data: any): string {
    return data + 'mm';
  }

  toggleAccumulated() {
    this.amount = !this.amount
    this.accumulated = !this.accumulated
    if (this.amount) {
      this.buttonText = 'Show accumulated'
    } else {
      this.buttonText = 'Show amount'
    }
  }

  private getCumulativeSeries(assetSamples: Array<Samples.AssetSample<Samples.PrecipitationSample>>): Array<{
    name: string,
    value: number
  }> {
    const handledDates: Map<string, number> = new Map()
    const series: { name: string, value: number }[] = []
    assetSamples.forEach(assetSample => {
      let amount = 0
      const date = new Date(assetSample.sample!.instant!)
      if (handledDates.has(date.toDateString())) {
        let x = handledDates.get(date.toDateString())!
        x += assetSample.sample?.value?.amount!
        amount += x
      } else {
        amount = assetSample.sample?.value?.amount!
      }
      handledDates.set(date.toDateString(), amount)
      series.push({
        name: date.toDateString(),
        value: amount
      })
    })
    return series
  }

  protected override lastSeenWithinThreshold(assetSamples: Samples.AssetSample<any>[]) {
    return lastSeenWithinThreshold(assetSamples, "Precipitation");
  }
}
